import {
    type FC,
    useContext,
    useState,
    useCallback,
    type ReactNode,
    type Dispatch,
    type SetStateAction,
} from 'react';
import { useRelayEnvironment } from 'react-relay';
import { defineMessages, useIntl, FormattedMessage } from 'dibs-react-intl';

import dibsCss from 'dibs-css';
import { BasicSelect } from 'dibs-elements/exports/BasicSelect';
import FormButton from './sharedComponents/FormButton';
import { Input } from 'dibs-elements/exports/Input';
import SellerFormContext from './SellerFormContext';
import { updateSellerApplicationLeadMutation } from './mutations/updateSellerApplicationLead';
import { sellerApplicationFormFieldsValidation } from '../helpers/sellerApplicationFormValidator';
import { trackSellerApplicationEvent } from '../helpers/sellerApplicationTracking';
import { useGetYesNoOptions, useGetNumRangeOptions } from '../helpers/sellerApplicationOptions';
import {
    type BusinessDetailsKeys,
    type EstimatedMonthlyUploads,
    type HoursManagePerWeek,
    estimatedMonthlyUploadsValues,
    hoursManagePerWeekValues,
} from './SellerApplicationFormReducer';

export const headerTitle = (
    <FormattedMessage
        defaultMessage="Business Details (cont.)"
        id="SellerApplicationForm.businessDetailsContinued.title"
    />
);

const messages = defineMessages({
    estimatedMonthlyUploadsPlaceHolder: {
        id: 'SellerApplicationForm.businessDetailsContinued.estimatedMonthlyUploadsLabel',
        defaultMessage: 'Estimated number of items you would list on 1stDibs every month',
    },
    isSellerOnOtherMarketPlacePlaceholder: {
        id: 'SellerApplicationForm.businessDetailsContinued.isSellerOnOtherMarketPlaceLabel',
        defaultMessage: 'Do you currently sell on other online marketplaces?',
    },
    otherMarketPlaceDetailsPlaceholder: {
        id: 'SellerApplicationForm.businessDetailsContinued.otherMarketPlaceDetailsLabel',
        defaultMessage: 'Which marketplaces?',
    },
    hoursManagePerWeekPlaceholder: {
        id: 'SellerApplicationForm.businessDetailsContinued.hoursManagePerWeekLabel',
        defaultMessage: 'How many hours per week could you spend managing your 1stDibs account?',
    },
});

type Props = {
    setFormError: Dispatch<SetStateAction<boolean>>;
};

const SellerApplicationBusinessDetailsContinued: FC<Props> = ({ setFormError }) => {
    const environment = useRelayEnvironment();
    const intl = useIntl();
    const { state, dispatch } = useContext(SellerFormContext);
    const yesNoOptions = useGetYesNoOptions();

    const {
        estimatedMonthlyUploads,
        isSellerOnOtherMarketPlace,
        otherMarketPlaceDetails,
        hoursManagePerWeek,
    } = state.formDataBusinessDetails;

    const [errorMessages, setErrorMessages] = useState<{
        [fieldName in BusinessDetailsKeys]?: ReactNode;
    }>({});
    const [isLoading, setIsLoading] = useState(false);

    const validateField = (name: BusinessDetailsKeys, value?: string | boolean): void => {
        const { errors } = sellerApplicationFormFieldsValidation({ [name]: value });
        const error = errors?.[name];

        setErrorMessages({
            ...errorMessages,
            [name]: error,
        });
    };

    async function submit(): Promise<void> {
        const { isValid, errors } = sellerApplicationFormFieldsValidation({
            estimatedMonthlyUploads,
            isSellerOnOtherMarketPlace,
            hoursManagePerWeek,
            ...(isSellerOnOtherMarketPlace && { otherMarketPlaceDetails }),
        });
        if (isValid) {
            setErrorMessages({});
            try {
                setIsLoading(true);
                await updateSellerApplicationLeadMutation(environment, {
                    recordId: state.leadId,
                    estimatedMonthlyUploads,
                    isSellerOnOtherMarketPlace,
                    otherMarketPlaceDetails,
                    hoursManagePerWeek,
                });
                trackSellerApplicationEvent({
                    type: 'professional',
                    action: 'step 2 submit',
                    interactionName: 'submit_page_2_part_2',
                    stepInteractionType: 'click on Continue',
                });
                dispatch({ type: 'SET_STEP', step: 'BUSINESS_STEP4' });
            } catch (err) {
                setIsLoading(false);
                setFormError(true);
            }
        } else {
            setIsLoading(false);
            setErrorMessages(errors);
        }
    }

    const setFormField = useCallback(
        (field: BusinessDetailsKeys, value?: string | boolean): void => {
            dispatch({
                type: 'SET_FORM_BUSINESS_DETAILS',
                formDataBusinessDetails: { [field]: value },
            });
        },
        [dispatch]
    );

    const setField = (name: BusinessDetailsKeys, value?: string | boolean): void => {
        setFormField(name, value);
        validateField(name, value);
    };

    return (
        <div className={dibsCss.mbLarge} data-tn="application-form-business-details">
            <div className={dibsCss.wFull}>
                <div className={dibsCss.mbSmall}>
                    <BasicSelect
                        dataTn="estimated-monthly-uploads"
                        name="estimated-monthly-uploads"
                        placeholder={intl.formatMessage(
                            messages.estimatedMonthlyUploadsPlaceHolder
                        )}
                        hasAnimatedPlaceholder
                        options={useGetNumRangeOptions(estimatedMonthlyUploadsValues)}
                        onChange={e => {
                            const value = e.target.value as EstimatedMonthlyUploads;
                            setField('estimatedMonthlyUploads', value);
                        }}
                        errorMessage={errorMessages?.estimatedMonthlyUploads}
                        hasError={!!errorMessages?.estimatedMonthlyUploads}
                        disabled={isLoading}
                        value={estimatedMonthlyUploads}
                    />
                </div>
                <div className={dibsCss.mbSmall}>
                    <BasicSelect
                        dataTn="is-seller-on-other-marketplaces"
                        name="is-seller-on-other-marketplaces"
                        placeholder={intl.formatMessage(
                            messages.isSellerOnOtherMarketPlacePlaceholder
                        )}
                        hasAnimatedPlaceholder
                        options={yesNoOptions}
                        onChange={e =>
                            setField('isSellerOnOtherMarketPlace', e.target.value === 'true')
                        }
                        errorMessage={errorMessages?.isSellerOnOtherMarketPlace}
                        hasError={!!errorMessages?.isSellerOnOtherMarketPlace}
                        disabled={isLoading}
                        value={
                            typeof isSellerOnOtherMarketPlace === 'boolean'
                                ? String(isSellerOnOtherMarketPlace)
                                : undefined
                        }
                    />
                </div>
                {isSellerOnOtherMarketPlace ? (
                    <div className={dibsCss.mbSmall}>
                        <Input
                            placeholder={intl.formatMessage(
                                messages.otherMarketPlaceDetailsPlaceholder
                            )}
                            hasAnimatedPlaceholder
                            dataTn="otherMarketPlaceDetails"
                            name="otherMarketPlaceDetails"
                            onChange={e => {
                                const value = e.target.value;
                                setField('otherMarketPlaceDetails', value);
                            }}
                            errorMessage={errorMessages?.otherMarketPlaceDetails}
                            hasError={!!errorMessages?.otherMarketPlaceDetails}
                            disabled={isLoading}
                            value={otherMarketPlaceDetails}
                            maxLength={1000}
                        />
                    </div>
                ) : null}
                <div className={dibsCss.mbSmall}>
                    <BasicSelect
                        dataTn="hours-to-manage-per-week"
                        name="hours-to-manage-per-week"
                        placeholder={intl.formatMessage(messages.hoursManagePerWeekPlaceholder)}
                        hasAnimatedPlaceholder
                        options={useGetNumRangeOptions(hoursManagePerWeekValues)}
                        onChange={e => {
                            const value = e.target.value as HoursManagePerWeek;
                            setField('hoursManagePerWeek', value);
                        }}
                        errorMessage={errorMessages?.hoursManagePerWeek}
                        hasError={!!errorMessages?.hoursManagePerWeek}
                        disabled={isLoading}
                        value={hoursManagePerWeek}
                    />
                </div>
                <FormButton isLoading={isLoading} onSubmit={submit} />
            </div>
        </div>
    );
};

export default SellerApplicationBusinessDetailsContinued;
